<template>
  <div class="records-display" data-testid="records-display">
    <a-result
      v-if="!results"
      title="No DNS Lookup Results"
      sub-title="Enter a domain name and select record types to begin"
      data-testid="dns-empty-state"
    >
      <template #icon>
        <database-outlined />
      </template>
    </a-result>
    
    <div v-else class="results-container" data-testid="dns-results-container">
      <div class="results-header">
        <a-space>
          <h3 data-testid="dns-results-domain">DNS Records for {{ results.domain }}</h3>
          <a-tag color="blue" data-testid="dns-query-time">{{ results.query_time.toFixed(3) }}s</a-tag>
        </a-space>
        
        <a-button @click="exportResults" size="small" data-testid="dns-export-button">
          <template #icon>
            <download-outlined />
          </template>
          Export JSON
        </a-button>
      </div>
      
      <a-collapse v-model:activeKey="activeKeys" class="records-collapse" data-testid="dns-records-collapse">
        <a-collapse-panel
          v-for="(records, recordType) in results.records"
          :key="recordType"
          :header="formatRecordTypeHeader(recordType, records || [])"
          :data-testid="`dns-record-panel-${recordType.toString().toLowerCase()}`"
        >
          <a-empty
            v-if="!records || records.length === 0"
            description="No records found"
            :image="Empty.PRESENTED_IMAGE_SIMPLE"
          />
          
          <record-table
            v-else
            :records="records"
            :record-type="recordType"
          />
        </a-collapse-panel>
      </a-collapse>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { DatabaseOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import { Empty, message } from 'ant-design-vue'
import RecordTable from './RecordTable.vue'
import type { DnsLookupResult } from '../types'

interface Props {
  results: DnsLookupResult | null
}

const props = defineProps<Props>()

const activeKeys = ref<string[]>([])

// Auto-open panels with records
const updateActiveKeys = () => {
  if (props.results?.records) {
    activeKeys.value = Object.keys(props.results.records).filter(
      key => props.results!.records[key as keyof typeof props.results.records]?.length
    )
  }
}

// Watch for results changes
computed(() => {
  if (props.results) {
    updateActiveKeys()
  }
})

const formatRecordTypeHeader = (recordType: string, records: any[]): string => {
  const count = records?.length || 0
  return `${recordType} (${count} record${count !== 1 ? 's' : ''})`
}

const exportResults = () => {
  if (!props.results) return
  
  try {
    const dataStr = JSON.stringify(props.results, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `dns-lookup-${props.results.domain}-${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    message.success('DNS records exported successfully')
  } catch (err) {
    console.error('Export failed:', err)
    message.error('Failed to export DNS records')
  }
}
</script>

<style scoped lang="scss">
.records-display {
  margin-top: 24px;
  
  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h3 {
      margin: 0;
      font-size: 18px;
    }
  }
  
  .records-collapse {
    background: #fff;
  }
}
</style>
